/**
 * Place search via Google's Places API (New) — for the availability modal's
 * Location field (TECH-AVAIL-02). Finds BUSINESS NAMES, not just addresses:
 * "Apple Ford" → "Apple Ford Pro Elite · Lakeville, MN"; picking one stores the
 * display name + coordinates, which makes the event a map waypoint.
 *
 * Same discipline as roadPath.ts: plain REST with the Maps key, biased to the
 * Twin Cities metro, sticky-off on 403 (API not enabled → the field quietly
 * stays a free-text input). Requires "Places API (New)" enabled for the key.
 */

export interface PlaceSuggestion {
  placeId: string;
  main: string; // "Apple Ford Pro Elite"
  secondary: string; // "Lakeville, MN, USA"
}

export interface PickedPlace {
  name: string; // display name
  address: string; // formatted address
  lat: number;
  lng: number;
}

let apiUnavailable = false;

function key(): string | undefined {
  return import.meta.env.VITE_GOOGLE_MAPS_KEY as string | undefined;
}

/** Autocomplete suggestions for a partial query (call debounced). */
export async function searchPlaces(input: string): Promise<PlaceSuggestion[]> {
  const k = key();
  if (!k || apiUnavailable || input.trim().length < 3) return [];
  try {
    const res = await fetch(
      "https://places.googleapis.com/v1/places:autocomplete",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": k,
        },
        body: JSON.stringify({
          input,
          // Bias hard to the Twin Cities metro so "Lennox" finds Mendota
          // Heights before Texas.
          locationBias: {
            circle: {
              center: { latitude: 44.95, longitude: -93.25 },
              radius: 50000,
            },
          },
          includedRegionCodes: ["us"],
        }),
      },
    );
    if (!res.ok) {
      if (res.status === 403) apiUnavailable = true;
      return [];
    }
    const data = await res.json();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return ((data?.suggestions ?? []) as any[])
      .map((s) => s.placePrediction)
      .filter(Boolean)
      .slice(0, 6)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((p: any) => ({
        placeId: p.placeId as string,
        main: (p.structuredFormat?.mainText?.text ?? p.text?.text ?? "") as string,
        secondary: (p.structuredFormat?.secondaryText?.text ?? "") as string,
      }));
  } catch {
    return [];
  }
}

/** Resolve a picked suggestion to its name + address + coordinates. */
export async function getPlace(placeId: string): Promise<PickedPlace | null> {
  const k = key();
  if (!k || apiUnavailable) return null;
  try {
    const res = await fetch(
      `https://places.googleapis.com/v1/places/${encodeURIComponent(placeId)}`,
      {
        headers: {
          "X-Goog-Api-Key": k,
          "X-Goog-FieldMask": "displayName,formattedAddress,location",
        },
      },
    );
    if (!res.ok) {
      if (res.status === 403) apiUnavailable = true;
      return null;
    }
    const data = await res.json();
    const lat = data?.location?.latitude;
    const lng = data?.location?.longitude;
    if (typeof lat !== "number" || typeof lng !== "number") return null;
    return {
      name: (data?.displayName?.text ?? "") as string,
      address: (data?.formattedAddress ?? "") as string,
      lat,
      lng,
    };
  } catch {
    return null;
  }
}
